import {
  type InteractionSource,
  type SelectionRange,
  type SelectionState
} from '../../../state/types.ts';
import { type ReducerResponse } from '../../types.ts';

import { closestBeforeSelectedPosition } from './focus-utils/index.ts';

type RemoveSingleItemRangeParams = {
  state: SelectionState;
  range: SelectionRange;
  source: InteractionSource;
  mode: SelectionState['mode'];
};

const axisFromMode = (mode: SelectionState['mode']) => (mode === 'row' ? 'row' : 'col');

/**
 * Removes a single-item range from selections.
 * @example
 * // User has rows 2 and 5 selected, clicks row 2 to deselect it
 * // Before: selections = [
 * //   { id: 'r1', anchor: { row: 2, col: 0 }, lead: { row: 2, col: 0 } },
 * //   { id: 'r2', anchor: { row: 5, col: 0 }, lead: { row: 5, col: 0 } }
 * // ]
 * // After: selections = [{ id: 'r2', anchor: { row: 5, col: 0 }, lead: { row: 5, col: 0 } }]
 *
 * @returns A reducer response with updated state and delta
 */
export function removeSingleItemRange({
  state,
  range,
  source,
  mode
}: RemoveSingleItemRangeParams): ReducerResponse {
  const updatedSelections = state.selections.filter((r) => r.id !== range.id);
  const noSelectionsLeft = updatedSelections.length === 0;
  const axis = axisFromMode(mode);
  const deselectedIndex =
    mode === 'row' ? range.anchor.row.index : range.anchor.col.index;
  const deselectedIndexHadFocus =
    state.focusedCell != null && state.focusedCell[axis].index === deselectedIndex;
  const shouldShiftFocus = deselectedIndexHadFocus || noSelectionsLeft;
  const focusAfterRemoval = noSelectionsLeft
    ? null
    : closestBeforeSelectedPosition(updatedSelections, axis, deselectedIndex);
  const focusedCell = shouldShiftFocus ? focusAfterRemoval : state.focusedCell;

  const delta: ReducerResponse['delta'] = {
    kind: 'selectionModified',
    source,
    rangeIdsToRemove: [range.id]
  };

  if (shouldShiftFocus) {
    delta.focusChange = {
      prev: state.focusedCell,
      next: focusedCell
    };
  }

  return {
    delta,
    next: {
      ...state,
      mode: updatedSelections.length === 0 ? 'idle' : mode,
      selections: updatedSelections,
      focusedCell,
      lastInteractionSource: source
    }
  };
}
